import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getCountryData, getStatesByCountry, getZipRegexByCountry } from '../../../../libs.jsx';
import SearchableSelect from '../../../../components/v2/SearchableSelect.jsx';
import '../campuses-page.css';
import HeaderInfoTip from '../../../../components/v2/HeaderInfoTip.jsx';
import '../../../../components/v2/drawer-tabs.css';
import Drawer from '../../../../components/v2/Drawer.jsx';

/**
 * CampusDrawer — slide-out drawer for the Add Campus form.
 *
 * Two tabs: "New Campus" (full form) and "Migrate Campus" (corp + name + GUID).
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   onSave: (data: object) => Promise<void>,
 *   corporations: Array<{ corporate_id: number, corporate_name: string }>,
 *   timezones: Array<{ timezone_id: number, timezone_name: string, timezone: string }>,
 * }} props
 */
const CampusDrawer = ({ isOpen, onClose, onSave, corporations, timezones }) => {
  const drawerRef = useRef(null);

  /* Tab state. 'new' is the 5.0 tab, 'migrate' is the 4.0 tab.
     Defaults to 4.0 because that is the common case: a 4.0 campus is set up in the
     NOC first and then migrated here, so most new campuses arrive through that tab.
     NOTE: the reset-on-open effect below sets this too. Changing only one of the two
     has no visible effect, because the effect runs on every open. */
  const [activeTab, setActiveTab] = useState('migrate'); // 'new' | 'migrate'

  // Shared form state
  const [corpId, setCorpId] = useState('');
  const [campusName, setCampusName] = useState('');
  const [saving, setSaving] = useState(false);

  // New Campus form state
  const [country, setCountry] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [timezoneId, setTimezoneId] = useState('');
  const [planExpire, setPlanExpire] = useState('');

  // Migrate Campus form state
  const [campusGuid, setCampusGuid] = useState('');

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setActiveTab('migrate');
      setCorpId('');
      setCampusName('');
      setCountry('');
      setAddress('');
      setCity('');
      setState('');
      setZipcode('');
      setTimezoneId('');
      setPlanExpire('');
      setCampusGuid('');
      setSaving(false);
    }
  }, [isOpen]);

  // Focus first input on open
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input, select');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Reset state field when country changes
  useEffect(() => {
    setState('');
    setZipcode('');
  }, [country]);

  // Validation
  const namePattern = /^[0-9a-zA-Z_ \-]+$/;
  const guidPattern = /^[0-9a-zA-Z_ \-]+$/;

  const isNameValid = campusName.trim() && namePattern.test(campusName.trim());
  const isGuidValid = campusGuid.trim() && guidPattern.test(campusGuid.trim());

  const zipRegex = country ? getZipRegexByCountry(country) : '';
  const isZipValid = zipcode.trim() && (!zipRegex || new RegExp(`^${zipRegex}$`).test(zipcode.trim()));

  const isNewFormValid = corpId && isNameValid && country && address.trim() && city.trim() && state && (zipRegex ? isZipValid : zipcode.trim()) && timezoneId && planExpire;
  const isMigrateFormValid = corpId && isGuidValid && timezoneId;

  const isFormValid = activeTab === 'new' ? isNewFormValid : isMigrateFormValid;

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!isFormValid) return;
      setSaving(true);

      const formData = activeTab === 'new'
        ? {
            campus_name: campusName.trim(),
            corporate_id: parseInt(corpId, 10),
            plan_expire: `${planExpire} 00:00:00`,
            campus_guid: '',
            address: address.trim(),
            city: city.trim(),
            state,
            zipcode: zipcode.trim(),
            timezone_id: parseInt(timezoneId, 10),
            country,
            migrate: false,
          }
        : {
            campus_name: '',
            corporate_id: parseInt(corpId, 10),
            plan_expire: '',
            campus_guid: campusGuid.trim(),
            address: '',
            city: '',
            state: '',
            zipcode: '',
            timezone_id: parseInt(timezoneId, 10),
            country: '',
            migrate: true,
          };

      onSave(formData).finally(() => setSaving(false));
    },
    [isFormValid, activeTab, corpId, campusName, country, address, city, state, zipcode, timezoneId, planExpire, campusGuid, onSave]
  );

  // Data helpers
  const countries = getCountryData();
  const states = country ? (getStatesByCountry(country) || []) : [];

  // Corporations, sorted alphabetically, for the searchable dropdown
  const corpOptions = useMemo(
    () => [...(corporations || [])]
      .sort((a, b) => (a.corporate_name || '').localeCompare(b.corporate_name || '', undefined, { sensitivity: 'base' }))
      .map((c) => ({
        value: c.corporate_id.toString(),
        label: c.corporate_name,
        searchText: c.corporate_name || '',
      })),
    [corporations]
  );

  // Sort timezones alphabetically
  const sortedTimezones = [...(timezones || [])].sort((a, b) =>
    (a.timezone_name || '').localeCompare(b.timezone_name || '', undefined, { sensitivity: 'base' })
  );

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      panelRef={drawerRef}
      ariaLabel="Add Campus"
      headerStart={(
        <div className="cp-drawer-title-row">
          <h2 className="dp-drawer__title">Add Campus</h2>
          <HeaderInfoTip
            label="About 5.0 and 4.0 campuses"
            spaced
            title="5.0 Campus"
            body="New campus created solely on the 5.0 platform. Does not require setup in the NOC and cannot be downgraded to 4.0 once created."
            sections={[
              { heading: '4.0 Campus', body: 'Backward-compatible campus running on the 4.0 platform. Must first be set up in the NOC and then migrated over using its GUID.' },
            ]}
          />
        </div>
      )}
      subheader={(
        <div className="dp-drawer-tabs">
          <button
            type="button"
            className={`dp-drawer-tab ${activeTab === 'new' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('new')}
          >
            5.0 Campus
          </button>
          <button
            type="button"
            className={`dp-drawer-tab ${activeTab === 'migrate' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('migrate')}
          >
            4.0 Campus
          </button>
        </div>
      )}
      rawBody
    >
      {/* Form */}
      <form onSubmit={handleSubmit} className="dp-drawer__body">
        {/* Corporation (shared) */}
        <div className="dp-drawer__field">
          <label htmlFor="cp-corp" className="dp-drawer__label">
            Corporation <span className="dp-drawer__required">*</span>
          </label>
          <SearchableSelect
            id="cp-corp"
            options={corpOptions}
            value={corpId}
            onChange={(val) => setCorpId(val || '')}
            placeholder="Search or select corporation..."
            ariaLabel="Corporation"
            required
            isClearable
          />
        </div>

        {/* Campus Name (5.0 only) */}
        {activeTab === 'new' ? (
          <div className="dp-drawer__field">
            <label htmlFor="cp-name" className="dp-drawer__label">
              Campus Name <span className="dp-drawer__required">*</span>
            </label>
            <input
              id="cp-name"
              type="text"
              value={campusName}
              onChange={(e) => setCampusName(e.target.value)}
              placeholder="Campus name"
              className={`dp-drawer__input ${campusName && !isNameValid ? 'is-invalid' : ''}`}
              required
            />
            {campusName && !isNameValid ? (
              <p className="dp-drawer__error">Only letters, numbers, spaces, dashes, and underscores</p>
            ) : null}
          </div>
        ) : null}

        {/* === NEW CAMPUS TAB === */}
        {activeTab === 'new' ? (
          <>
            {/* Country */}
            <div className="dp-drawer__field">
              <label htmlFor="cp-country" className="dp-drawer__label">
                Country <span className="dp-drawer__required">*</span>
              </label>
              <select
                id="cp-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="dp-drawer__select"
                required
              >
                <option value="">Select country...</option>
                {countries.map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            </div>

            {/* Street Address */}
            <div className="dp-drawer__field">
              <label htmlFor="cp-address" className="dp-drawer__label">
                Street Address <span className="dp-drawer__required">*</span>
              </label>
              <input
                id="cp-address"
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Street address"
                className="dp-drawer__input"
                required
              />
            </div>

            {/* City + State + Zip (row) */}
            <div className="cp-drawer-address-row">
              <div className="dp-drawer__field" style={{ flex: '2' }}>
                <label htmlFor="cp-city" className="dp-drawer__label">
                  City <span className="dp-drawer__required">*</span>
                </label>
                <input
                  id="cp-city"
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  className="dp-drawer__input"
                  required
                />
              </div>
              <div className="dp-drawer__field" style={{ flex: '1' }}>
                <label htmlFor="cp-state" className="dp-drawer__label">
                  State <span className="dp-drawer__required">*</span>
                </label>
                <select
                  id="cp-state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="dp-drawer__select"
                  required
                  disabled={!country}
                >
                  <option value="">...</option>
                  {states.map(([code, name]) => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>
              <div className="dp-drawer__field" style={{ flex: '1' }}>
                <label htmlFor="cp-zip" className="dp-drawer__label">
                  Zip <span className="dp-drawer__required">*</span>
                </label>
                <input
                  id="cp-zip"
                  type="text"
                  value={zipcode}
                  onChange={(e) => setZipcode(e.target.value)}
                  placeholder="Zip"
                  className={`dp-drawer__input ${zipcode && !isZipValid ? 'is-invalid' : ''}`}
                  required
                />
              </div>
            </div>

            {/* Timezone */}
            <div className="dp-drawer__field">
              <label htmlFor="cp-timezone" className="dp-drawer__label">
                Timezone <span className="dp-drawer__required">*</span>
              </label>
              <select
                id="cp-timezone"
                value={timezoneId}
                onChange={(e) => setTimezoneId(e.target.value)}
                className="dp-drawer__select"
                required
              >
                <option value="">Select timezone...</option>
                {sortedTimezones.map((tz) => (
                  <option key={tz.timezone_id} value={tz.timezone_id}>
                    {tz.timezone_name} ({tz.timezone})
                  </option>
                ))}
              </select>
            </div>

            {/* Service Plan Expiry */}
            <div className="dp-drawer__field">
              <label htmlFor="cp-planexpire" className="dp-drawer__label">
                Service Plan Expiry <span className="dp-drawer__required">*</span>
              </label>
              <input
                id="cp-planexpire"
                type="date"
                value={planExpire}
                onChange={(e) => setPlanExpire(e.target.value)}
                className="dp-drawer__input"
                required
              />
            </div>
          </>
        ) : null}

        {/* === MIGRATE CAMPUS TAB === */}
        {activeTab === 'migrate' ? (
          <>
            {/* Campus 2.0 GUID */}
            <div className="dp-drawer__field">
              <label htmlFor="cp-guid" className="dp-drawer__label">
                Campus GUID from NOC <span className="dp-drawer__required">*</span>
              </label>
              <input
                id="cp-guid"
                type="text"
                value={campusGuid}
                onChange={(e) => setCampusGuid(e.target.value)}
                placeholder="Campus 2.0 GUID"
                className={`dp-drawer__input ${campusGuid && !isGuidValid ? 'is-invalid' : ''}`}
                required
              />
              {campusGuid && !isGuidValid ? (
                <p className="dp-drawer__error">Only letters, numbers, spaces, dashes, and underscores</p>
              ) : null}
            </div>

            {/* Timezone */}
            <div className="dp-drawer__field">
              <label htmlFor="cp-migrate-timezone" className="dp-drawer__label">
                Timezone <span className="dp-drawer__required">*</span>
              </label>
              <select
                id="cp-migrate-timezone"
                value={timezoneId}
                onChange={(e) => setTimezoneId(e.target.value)}
                className="dp-drawer__select"
                required
              >
                <option value="">Select timezone...</option>
                {sortedTimezones.map((tz) => (
                  <option key={tz.timezone_id} value={tz.timezone_id}>
                    {tz.timezone_name} ({tz.timezone})
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : null}

        {/* Footer */}
        <div className="dp-drawer__footer">
          <button
            type="button"
            onClick={onClose}
            className="dp-btn dp-btn--secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid || saving}
            className="dp-btn dp-btn--primary"
          >
            {saving ? 'Adding...' : activeTab === 'new' ? 'Add Campus' : 'Migrate Campus'}
          </button>
        </div>
      </form>
    </Drawer>
  );
};

export default CampusDrawer;
